import { useQuery } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { AiServiceSummary } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/forms';
import { Modal } from '../../ui/Modal';
import { PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/** IDN-06: named AI service identities, credential lifecycle and grant summaries. */
export function AiServicesPage() {
  usePageTitle('AI services');
  useCrumbs([{ label: 'Administration' }, { label: 'AI services' }]);
  const admin = useIsAdmin();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['ai-services'],
    queryFn: () => api.get<AiServiceSummary[]>('/admin/ai-services'),
    enabled: admin,
  });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const create = useAction(() => api.post<{ id: string }>('/admin/ai-services', { name, description }), {
    success: 'AI service registered',
    invalidate: [['ai-services']],
    onSuccess: (r) => navigate(`/admin/ai-services/${r.id}`),
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="AI services"
        subtitle="Active services read all project business records. Write access is granted per project."
        actions={<Button onClick={() => setCreating(true)}>Register AI service</Button>}
      />
      <QueryView query={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState icon={Bot} title="No AI services yet">
              Register a service identity, issue a credential, and grant project capabilities.
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>State</th>
                    <th className="num">Active credentials</th>
                    <th>Next expiry</th>
                    <th>Last used</th>
                    <th>Write grants</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((s) => (
                    <tr
                      key={s.id}
                      className="clickable"
                      onClick={() => navigate(`/admin/ai-services/${s.id}`)}
                    >
                      <td>
                        <a
                          href={`/admin/ai-services/${s.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/admin/ai-services/${s.id}`);
                          }}
                          className="primary-cell"
                        >
                          {s.name}
                        </a>
                        <div className="small muted">{s.description}</div>
                      </td>
                      <td>{s.active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}</td>
                      <td className="num">{s.activeCredentials}</td>
                      <td className="small">
                        <Timestamp value={s.nextCredentialExpiry} dateOnly />
                      </td>
                      <td className="small">
                        <Timestamp value={s.lastUsedAt} relative />
                      </td>
                      <td className="small">
                        {s.grants.length ? (
                          s.grants.map((g) => `${g.projectKey} (${g.capabilities})`).join(', ')
                        ) : (
                          <span className="muted">None</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryView>
      {creating && (
        <Modal
          title="Register AI service"
          onClose={() => setCreating(false)}
          actions={
            <>
              <Button variant="secondary" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button onClick={() => create.mutate(undefined)} loading={create.isPending} disabled={!name}>
                Register
              </Button>
            </>
          }
        >
          <Field label="Name" hint="Shown as the actor on comments, claims and evidence.">
            {(p) => (
              <Input
                {...p}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. builder-agent"
                data-autofocus
              />
            )}
          </Field>
          <Field label="Description" optional>
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
          <p className="small muted">New services have no write grants.</p>
          <ErrorAlert error={create.error} />
        </Modal>
      )}
    </div>
  );
}
